import { z } from "zod/v4";

import {
  CustomEventDefinition,
  CustomEventDefinitionSchema,
  CustomEventDtoSchema,
} from "../../dtos/analytics-events";
import { ExperienceCategoryList } from "../../dtos/experience-categories";
import { ExperienceDtoSchema } from "../../dtos/experiences";
import { DownloadExperienceResponseBodyType } from "../../functions/experience-files";
import { castToBool, castToNumber, castToString } from "../../utils";

import {
  AnalyticsResourceTypeV2List,
  AnalyzeContext,
  CastedTimesTinybird,
  ChannelTypeSchema,
  ConversionStageReachedList,
  CustomEventsFilterTypeList,
  DatasetIdList,
  DeviceTypeList,
  LandingPagePayloadSchema,
  UrlParamPayloadSchema,
  VisitorTypeSchema,
} from "./base";

export const ExperienceSpanSchema = z.object({
  start_ts: z.number(),
  end_ts: z.number(),
});

// Base
export const BaseExperienceTinybirdAnalyticsPayloadSchema = z.object({
  datasetId: z.enum(DatasetIdList),
  startTs: castToString.or(z.number()).nullable(),
  endTs: castToString.or(z.number()).nullable(),
  shopifyDomain: z.string(),
  latestRunTs: z.string(),
  timezone: z.string(),
  urlParam: UrlParamPayloadSchema,
  landingPage: LandingPagePayloadSchema.nullish(),
  deviceType: z.enum(DeviceTypeList).nullable(),
  visitorType: z.enum(VisitorTypeSchema).nullable(),
  sourceSitesOrChannels: z.enum(ChannelTypeSchema).array(),
  shippingExpensePerOrder: castToNumber.or(z.string()).nullable(),
  transactionFeePct: castToNumber.or(z.string()).nullable(),
  countryCodes: z.string().array(),
  metrics: z.string().array().default([]),
  userBehavior: z.object({
    viewedCheckout: z.boolean().nullable(),
    addedToCart: z.boolean().nullable(),
    viewedProductPage: z.boolean().nullable().optional(),
    viewedCollectionPage: z.boolean().nullable().optional(),
  }),
  maxZScore: castToNumber.nullable(),
  experienceId: z.uuid(),
  triggeredCustomEvents: z.string().array().default([]),
  audience: z.string().nullish(),
  interval: z.string().nullish(),
  topN: z.any().optional(),
  histogramMetric: z.string().nullish(),
  kind: z.string().nullish(),
  experimentProductsOnly: castToBool.nullable(),
  reachedCheckoutStage: z.enum(ConversionStageReachedList).nullable(),
  return_variants: z.boolean().optional(),
  customEventsFilterType: z
    .enum(CustomEventsFilterTypeList)
    .default("all")
    .optional(),
});

export type BaseExperimentTinybirdAnalyticsResponseType = z.infer<
  typeof BaseExperienceTinybirdAnalyticsPayloadSchema
>;
// Node Backend to Analytics Request
export const AnalyzeExperienceBackendToTinybirdRequestSchema =
  BaseExperienceTinybirdAnalyticsPayloadSchema.extend({
    controlVariationId: z.uuid(),
    experienceStartTs: z.number(),
    experienceSpans: ExperienceSpanSchema.array().default([]),
    onlyProductHandles: z.string().array().nullish(),
    customEventDefinitions: CustomEventDefinitionSchema.array().optional(),
    excludeIpAddresses: z.string().array(),
    useOriginal: z.boolean(),
    excludeOrderTags: z.string().array(),
    onlyProductIds: z.string().array().nullish(),
    experienceType: z.enum(ExperienceCategoryList),
    isHeadless: z.boolean(),
    isHighVolume: z.boolean().optional().default(false),
  });

export type AnalyzeExperienceBackendToTinybirdRequestType = z.infer<
  typeof AnalyzeExperienceBackendToTinybirdRequestSchema
>;

// Frontend to Node Backend Request
export const AnalyzeExperienceFrontendRequestBodySchema =
  BaseExperienceTinybirdAnalyticsPayloadSchema.omit({
    shopifyDomain: true,
    latestRunTs: true,
    timezone: true,
  }).extend({
    baselineVariationId: z.uuid().optional(),
    customEvents: CustomEventDtoSchema.array().optional(),
    resourceType: z.enum(AnalyticsResourceTypeV2List),
  });

export const FileExportExperienceFrontendRequestBodySchema =
  AnalyzeExperienceFrontendRequestBodySchema.extend({
    exportType: z.enum([
      "ordersExport",
      "lineItemsExport",
      "ordersReconciliationExport",
    ]),
  });

export type FileExportExperienceFrontendRequestBodyType = z.infer<
  typeof FileExportExperienceFrontendRequestBodySchema
>;

export type FileExportExperienceFrontendResponseBodyType =
  DownloadExperienceResponseBodyType;

export type FileExportExperienceAnalyticsResponseBodyType = {
  job_url?: string;
  s3_bucket?: string;
  s3_path?: string;
  message: string;
};

export type AnalyzeExperienceFrontendTinybirdRequestBodyType = z.infer<
  typeof AnalyzeExperienceFrontendRequestBodySchema
> &
  CastedTimesTinybird;

export const AnalyzeExperienceTinybirdContextSchema = AnalyzeContext.extend({
  experience: ExperienceDtoSchema,
  onlyProductIds: z.string().array().nullish(),
  baselineVariationId: z.uuid(),
  numVariations: z.number(),
  formattedCustomEventDefinitions:
    CustomEventDefinitionSchema.array().optional(),
});

export type AnalyzeExperienceTinybirdContextType = z.infer<
  typeof AnalyzeExperienceTinybirdContextSchema
>;
